import { z } from 'zod';
import {
  asRec,
  isRec,
  resolverAlias,
  toBool,
  toFecha,
  toInstante,
  toNum,
  toStr,
  toStrList,
} from './adaptHelpers';
import type { AlertaCanal, PerfilTipo, Tendencia } from './types';

/**
 * Esquemas zod de lo que devuelve la API (snake_case). Son TOLERANTES a propósito (docs/CONTRATO_API.md §4):
 * los campos opcionales que no sirven se vuelven null en vez de romper, y solo se rechaza lo imprescindible
 * (p. ej. una fila sin rubro, sin fecha válida o sin ningún precio). Los adaptadores descartan esas filas.
 */

/* ───────────── Primitivas ───────────── */
export const textoReq = z.unknown().transform(toStr).pipe(z.string());
export const textoOpc = z.unknown().transform(toStr);
export const numeroReq = z.unknown().transform(toNum).pipe(z.number());
export const numeroOpc = z.unknown().transform(toNum);
export const fechaReq = z.unknown().transform(toFecha).pipe(z.string());
export const fechaOpc = z.unknown().transform(toFecha);
export const instanteOpc = z.unknown().transform(toInstante);
export const listaTextos = z.unknown().transform(toStrList);
export const booleano = (porDefecto: boolean) =>
  z.unknown().transform((v) => toBool(v, porDefecto));

const enumTolerante = <T extends string>(valores: readonly T[], porDefecto: T) =>
  z
    .unknown()
    .transform((v): T =>
      typeof v === 'string' && (valores as readonly string[]).includes(v) ? (v as T) : porDefecto,
    );
export const PERFILES_TIPO = [
  'ganadero_carne',
  'lechero',
  'quesero',
  'porcicultor',
  'bufalero',
  'agricultor',
  'comerciante',
] as const satisfies readonly PerfilTipo[];

/** Ids retirados que aún pueden venir de datos guardados: se leen como su sucesor. */
const PERFILES_LEGADO: Readonly<Record<string, PerfilTipo>> = {
  comprador_frigorifico: 'comerciante',
};

/** Perfil válido (o su sucesor si el id fue retirado); `null` si no se reconoce. */
export const normalizarPerfil = (v: unknown): PerfilTipo | null => {
  if (typeof v !== 'string') return null;
  if ((PERFILES_TIPO as readonly string[]).includes(v)) return v as PerfilTipo;
  return PERFILES_LEGADO[v] ?? null;
};
const CANALES = ['app', 'email', 'push'] as const satisfies readonly AlertaCanal[];
const TENDENCIAS = ['sube', 'baja', 'estable', 'sin_dato'] as const satisfies readonly Tendencia[];

/* ───────────── Precios (ultimos / historico) ───────────── */
const ALIAS_FILA = {
  precio_min: ['min', 'minimo', 'precio_minimo', 'precioMin'],
  precio_max: ['max', 'maximo', 'precio_maximo', 'precioMax'],
  precio_prom: ['prom', 'promedio', 'precio_promedio', 'precioProm'],
  tasa_bcv: ['tasaBcv', 'tasa'],
  min_bs: ['minBs'],
  max_bs: ['maxBs'],
  prom_bs: ['promBs'],
  fecha_fin: ['fechaFin'],
};

const FILA_COMUN = {
  presentacion: textoOpc,
  unidad: textoOpc,
  moneda: textoOpc,
  precio_min: numeroOpc,
  precio_max: numeroOpc,
  precio_prom: numeroOpc,
  tasa_bcv: numeroOpc,
  min_bs: numeroOpc,
  max_bs: numeroOpc,
  prom_bs: numeroOpc,
  region: textoOpc,
  fecha: fechaReq,
  fecha_fin: fechaOpc,
  nota: textoOpc,
};

interface PreciosCrudos {
  precio_min: number | null;
  precio_max: number | null;
  precio_prom: number | null;
}
const hayPrecio = (f: PreciosCrudos): boolean =>
  f.precio_min !== null || f.precio_max !== null || f.precio_prom !== null;
const sinNegativos = (f: PreciosCrudos): boolean =>
  [f.precio_min, f.precio_max, f.precio_prom].every((n) => n === null || n >= 0);

export const filaPrecioSchema = z.preprocess(
  (raw) => resolverAlias(raw, ALIAS_FILA),
  z
    .object({ ...FILA_COMUN, rubro: textoReq, categoria: textoReq, fuente: textoReq })
    .refine(hayPrecio, { message: 'fila sin precio' })
    .refine(sinNegativos, { message: 'precio negativo' }),
);
export type FilaPrecioCruda = z.infer<typeof filaPrecioSchema>;

/** Filas de histórico: rubro/categoría/fuente pueden venir omitidos si la consulta ya los fijaba. */
export const filaHistoricoSchema = z.preprocess(
  (raw) => resolverAlias(raw, ALIAS_FILA),
  z
    .object({ ...FILA_COMUN, rubro: textoOpc, categoria: textoOpc, fuente: textoOpc })
    .refine(hayPrecio, { message: 'fila sin precio' })
    .refine(sinNegativos, { message: 'precio negativo' }),
);
export type FilaHistoricoCruda = z.infer<typeof filaHistoricoSchema>;

/* ───────────── BCV ───────────── */
export const tasaBcvSchema = z.preprocess(
  (raw) =>
    resolverAlias(raw, {
      usd: ['tasa', 'valor', 'dolar', 'promedio', 'precio'],
      fecha: ['fecha_valor', 'date'],
      eur: ['euro'],
    }),
  z.object({
    fecha: fechaReq,
    usd: numeroReq.refine((n) => n > 0, { message: 'tasa no positiva' }),
    eur: numeroOpc,
    fuente: textoOpc,
  }),
);

/* ───────────── Fuentes ───────────── */
const ALIAS_FUENTE = {
  fuente: ['clave', 'slug', 'id'],
  ultimo_dato: ['ultima_fecha', 'fecha_ultimo_dato', 'ultimaFecha'],
  permiso: ['estado_permiso'],
};
export const fuenteSchema = z.preprocess(
  (raw) => resolverAlias(raw, ALIAS_FUENTE),
  z.object({
    fuente: textoOpc,
    nombre: textoOpc,
    tipo: textoOpc,
    url: textoOpc,
    region: textoOpc,
    frecuencia: textoOpc,
    permiso: textoOpc,
    ultimo_dato: fechaOpc,
  }),
);

export const fuenteDetalleSchema = z.object({
  clave: textoReq,
  nombre: textoOpc,
  tipo: textoOpc,
  url: textoOpc,
  region: textoOpc,
  frecuencia: textoOpc,
  frecuencia_dias: numeroOpc,
  permiso: textoOpc,
  ultimo_dato: fechaOpc,
  dias_desde_ultimo: numeroOpc,
  frescura: enumTolerante(
    ['al_dia', 'atrasada', 'desactualizada', 'sin_datos'] as const,
    'sin_datos',
  ),
  filas: numeroOpc,
  rubros: listaTextos,
  regiones: listaTextos,
  activa: booleano(true),
  notas_publicas: textoOpc,
});

/* ───────────── Cuenta ───────────── */
export const usuarioSchema = z.object({
  id: textoReq,
  email: textoReq,
  nombre: textoOpc,
  rol: enumTolerante(['usuario', 'admin'] as const, 'usuario'),
  perfil_tipo: z.unknown().transform(normalizarPerfil),
  region_principal: textoOpc,
  email_verificado: booleano(false),
  creado: instanteOpc,
});

export const tokensSchema = z.object({
  access_token: textoReq,
  refresh_token: textoOpc,
  expires_in: numeroOpc,
});

export const sesionSchema = z.object({
  id: textoReq,
  creada: instanteOpc,
  ultimo_uso: instanteOpc,
  user_agent: textoOpc,
  actual: booleano(false),
});

/* ───────────── Alertas y notificaciones ───────────── */
export const filtroAlertaSchema = z.object({
  rubro: textoReq,
  categoria: textoReq,
  presentacion: textoOpc,
  region: textoOpc,
  fuente: textoOpc,
});

export const alertaSchema = z.object({
  id: textoReq,
  nombre: textoOpc,
  activa: booleano(true),
  objetivo: enumTolerante(['precio', 'bcv'] as const, 'precio'),
  filtro: z.unknown().transform((v) => {
    const parsed = isRec(v) ? filtroAlertaSchema.safeParse(v) : null;
    return parsed?.success ? parsed.data : null;
  }),
  metrica: enumTolerante(['prom', 'min', 'max'] as const, 'prom'),
  moneda: enumTolerante(['USD', 'VES'] as const, 'USD'),
  condicion: enumTolerante(['sobre', 'bajo', 'sube_pct', 'baja_pct'] as const, 'sobre'),
  umbral: numeroReq,
  periodo_dias: numeroOpc,
  canales: z
    .unknown()
    .transform((v) =>
      toStrList(v).filter((c): c is AlertaCanal => (CANALES as readonly string[]).includes(c)),
    ),
  cooldown: enumTolerante(['una_vez', 'cada_dato', 'diaria'] as const, 'diaria'),
  estado: enumTolerante(['armada', 'disparada', 'pausada'] as const, 'armada'),
  creada: instanteOpc,
  ultima_evaluacion: instanteOpc,
  ultima_disparada: instanteOpc,
  ultimo_valor: numeroOpc,
});

/** Solo rutas internas ("/precios?..."): evita `javascript:` y redirecciones a otros sitios. */
export const enlaceInterno = z.unknown().transform((v): string | null => {
  const t = toStr(v);
  return t !== null && t.startsWith('/') && !t.startsWith('//') && !t.includes('\\') ? t : null;
});

export const notificacionSchema = z.object({
  id: textoReq,
  tipo: enumTolerante(['alerta', 'sistema', 'resumen'] as const, 'sistema'),
  titulo: textoReq,
  cuerpo: z.unknown().transform((v) => toStr(v) ?? ''),
  datos: z.unknown().transform((v) => asRec(v)),
  enlace: enlaceInterno,
  creada: instanteOpc,
  leida: booleano(false),
});

/* ───────────── Analítica ───────────── */
export const variacionSchema = z.object({
  rubro: textoReq,
  categoria: textoReq,
  presentacion: textoOpc,
  region: textoOpc,
  fuente: textoOpc,
  unidad: textoOpc,
  actual: numeroReq,
  fecha_actual: fechaReq,
  previo: numeroOpc,
  fecha_previo: fechaOpc,
  dif_abs: numeroOpc,
  dif_pct: numeroOpc,
  tendencia: enumTolerante(TENDENCIAS, 'sin_dato'),
  puntos: numeroOpc,
  spark: z
    .unknown()
    .transform((v) =>
      Array.isArray(v) ? v.map(toNum).filter((n): n is number => n !== null) : [],
    ),
});
export type VariacionCruda = z.infer<typeof variacionSchema>;

/* ───────────── Capacidades ───────────── */
const seccion = z.unknown().transform(asRec);
export const capacidadesSchema = z.object({
  version: z.unknown().transform((v) => toStr(v) ?? '0'),
  auth: seccion,
  push: seccion,
  email: seccion,
  mercado: seccion,
  admin: seccion,
  limites: seccion,
});
